/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { PublicWarningStatus } from '../../store/warningsForm/types';
import { isEndedStatus } from '../../store/warningsForm/utils';
import {
  LevelColors,
  MinorLevelColors,
  WarningLevel,
} from '../PublicWarningsForm/LevelField';

/**
 * Timeline Block Color Theme
 *
 * Organized by warning type (public warnings by level, aviation)
 * Each type has colors for each status: published, draft, expired
 */

export interface BlockStyle {
  background: string;
  border: string;
  borderColor: string;
  iconColor: string;
  letterColor: string;
}

const draftStripes = (stripeColor: string, bgColor: string): string =>
  `repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 8px,
    ${stripeColor} 8px,
    ${stripeColor} 9px
  ), ${bgColor}`;

const TimelineColors = {
  minor: {
    published: {
      background: MinorLevelColors.color,
      border: MinorLevelColors.borderColor,
      iconColor: MinorLevelColors.textColor!,
      letterColor: MinorLevelColors.textColor!,
    },
    draft: {
      background: '#FCFFEB',
      stripeColor: 'rgba(255, 255, 255, 0.5)',
      border: MinorLevelColors.borderColor,
      iconColor: MinorLevelColors.color,
      letterColor: MinorLevelColors.color,
    },
    expired: {
      background: '#EFEFEF',
      border: MinorLevelColors.borderColor,
      iconColor: MinorLevelColors.color,
      letterColor: MinorLevelColors.color,
    },
  },
  // Public warnings - Extreme (Red)
  extreme: {
    published: {
      background: LevelColors.extreme.color,
      border: LevelColors.extreme.borderColor,
      iconColor: LevelColors.extreme.textColor!,
      letterColor: LevelColors.extreme.textColor!,
    },
    draft: {
      background: '#F1D0D0',
      stripeColor: 'rgba(255, 255, 255, 0.5)',
      border: LevelColors.extreme.borderColor,
      iconColor: LevelColors.extreme.color,
      letterColor: LevelColors.extreme.color,
    },
    expired: {
      background: '#EFEFEF',
      border: LevelColors.extreme.borderColor,
      iconColor: LevelColors.extreme.color,
      letterColor: LevelColors.extreme.color,
    },
  },

  // Public warnings - Severe (Orange)
  severe: {
    published: {
      background: LevelColors.severe.color,
      border: LevelColors.severe.borderColor,
      iconColor: 'white',
      letterColor: 'white',
    },
    draft: {
      background: '#FADDC2',
      stripeColor: 'rgba(255, 255, 255, 0.5)',
      border: '#E27000',
      iconColor: '#9C4D00',
      letterColor: '#9C4D00',
    },
    expired: {
      background: '#EFEFEF',
      border: '#E27000',
      iconColor: '#9C4D00',
      letterColor: '#9C4D00',
    },
  },

  // Public warnings - Moderate (Yellow)
  moderate: {
    published: {
      background: LevelColors.moderate.color,
      border: LevelColors.moderate.borderColor,
      iconColor: '#3A3400',
      letterColor: '#3A3400',
    },
    draft: {
      background: '#FFFACE',
      stripeColor: 'rgba(250, 226, 28, 0.5)', // Yellow stripes
      border: LevelColors.moderate.borderColor,
      iconColor: '#3A3400',
      letterColor: '#3A3400',
    },
    expired: {
      background: '#EFEFEF',
      border: LevelColors.moderate.borderColor,
      iconColor: '#3A3400',
      letterColor: '#3A3400',
    },
  },

  // Aviation (SIGMET/AIRMET) - Blue
  aviation: {
    published: {
      background: '#186DFF',
      border: '#0800B9',
      iconColor: '#FFFFFF',
      letterColor: '#FFFFFF',
    },
    draft: {
      background: '#E3EDFF',
      stripeColor: 'rgba(255, 255, 255, 0.5)',
      border: '#0800B9',
      iconColor: '#0800B9',
      letterColor: '#0800B9',
    },
    expired: {
      background: '#EFEFEF',
      border: '#0800B9',
      iconColor: '#186DFF',
      letterColor: '#186DFF',
    },
  },

  // Default/fallback
  default: {
    published: {
      background: '#FFFFFF',
      border: '#AAAAAA',
      iconColor: '#333333',
      letterColor: '#333333',
    },
    draft: {
      background: '#FFFFFF',
      stripeColor: 'rgba(255, 255, 255, 0.5)',
      border: '#AAAAAA',
      iconColor: '#333333',
      letterColor: '#333333',
    },
    expired: {
      background: '#EFEFEF',
      border: '#AAAAAA',
      iconColor: '#333333',
      letterColor: '#333333',
    },
  },
};

const getThemeForLevel = (
  level: WarningLevel | null,
  isAviation: boolean,
  isExpiryMessage: boolean,
): (typeof TimelineColors)['extreme'] => {
  if (isAviation) {
    return TimelineColors.aviation;
  }
  if (isExpiryMessage) {
    return TimelineColors.minor;
  }
  switch (level) {
    case WarningLevel.extreme:
      return TimelineColors.extreme;
    case WarningLevel.severe:
      return TimelineColors.severe;
    case WarningLevel.moderate:
      return TimelineColors.moderate;
    default:
      return TimelineColors.default;
  }
};

export const getBlockStyle = (
  level: WarningLevel | null,
  status: PublicWarningStatus | undefined,
  isAviation: boolean,
  isExpiryMessage = false,
): BlockStyle => {
  const theme = getThemeForLevel(level, isAviation, isExpiryMessage);

  if (
    status === PublicWarningStatus.DRAFT ||
    status === PublicWarningStatus.DRAFT_PUBLISHED
  ) {
    const { stripeColor, background, border, iconColor, letterColor } =
      theme.draft;
    return {
      background: draftStripes(stripeColor, background),
      border: `1px solid ${border}`,
      borderColor: border,
      iconColor,
      letterColor,
    };
  }

  if (isEndedStatus(status)) {
    return {
      background: theme.expired.background,
      border: `1px solid ${theme.expired.border}`,
      borderColor: theme.expired.border,
      iconColor: theme.expired.iconColor,
      letterColor: theme.expired.letterColor,
    };
  }

  // Published (default)
  return {
    background: theme.published.background,
    border: `1px solid ${theme.published.border}`,
    borderColor: theme.published.border,
    iconColor: theme.published.iconColor,
    letterColor: theme.published.letterColor,
  };
};
